// Global packages or components
import { useLoaderData } from '@remix-run/react'
import { getAllProductsPage } from '~/sanity'
import { formatListingFields, getMeta, getSchema, Schema } from '~/utils'
import { ProductListing, Breadcrumbs } from '~/components'
import { getListingProducts } from '~/.server'
import { usePageInfo } from '~/hooks'

// Types
import type {
  MetaFunction,
  LoaderFunction,
  LoaderFunctionArgs,
  HeadersFunction,
} from '@remix-run/node'

// Route loader
export const loader: LoaderFunction = async ({
  request,
}: LoaderFunctionArgs) => {
  const env = process.env
  const url = new URL(request.url)
  const search = new URLSearchParams(url.searchParams)

  // Sanity fetch
  const data = await getAllProductsPage({
    dataset: env?.ENVIRONMENT,
    projectId: env?.SANITY_PROJECT,
  })

  // Page not found
  if (!data) {
    throw new Response('Not Found', {
      status: 404,
    })
  }

  const dataFormatted = formatListingFields({ ...data })

  // Variant level search, scoped by the query rather than a category
  const listing = await getListingProducts({
    request,
    searchTerm: search.get('query') ?? undefined,
    categoryFilters: data?.filters ?? [],
  })

  // Return data
  return Response.json({
    title: data?.title,
    seo: data?.seo,
    content: data?.content,
    filters: data?.filters,
    filterLabels: data?.filterLabels,
    subCategories: dataFormatted?.subCategories,
    banners: dataFormatted?.banners,
    query: search.get('query') ?? '',
    listing,
  })
}


/*
  Without a headers export Remix discards whatever a loader put on the response
  (getDocumentHeaders, headers.js). Stated explicitly rather than left to the
  CDN default, because app/root.tsx attaches a session cookie and a CSRF token
  to every response and a shared cache must never store those. Listing searches
  are cached upstream instead, in .server/brenda/listingCache.
*/
export const headers: HeadersFunction = () => ({
  'Cache-Control': 'private, no-store',
})

// Route meta
export const meta: MetaFunction = ({ matches, data, location }) => {
  return getMeta({
    matches,
    data,
    location,
  })
}

// Route view
export default function Index() {
  const {
    title,
    seo,
    content,
    filterLabels,
    subCategories,
    banners,
    query,
    listing,
  } = useLoaderData<typeof loader>()

  usePageInfo({ pageType: 'Search Results' })

  const jsonLd = getSchema({
    type: 'page',
    page: seo,
  })

  return (
    <main>
      <Schema data={jsonLd} />
      <Breadcrumbs path="/search-results" />
      <ProductListing
        banner={banners?.header}
        bannerSidebar={banners?.sidebar}
        subCategories={subCategories}
        content={content}
        title={query ? `Search results for "${query}"` : title}
        filterLabels={filterLabels}
        listing={listing}
      />
    </main>
  )
}
